// @flow
import Handler from "./handler.js";

import * as Spring from "../pagehandler/math/springconstants.js";
import Kinetics from "../pagehandler/math/kinetics.js";

const THRESHOLD = 0.6;

export default class Touch extends Handler {
	// XXX: https://github.com/facebook/flow/issues/3076
	handleTouchStart: (ev: TouchEvent) => void;
	handleTouchMove: (ev: TouchEvent) => void;
	handleTouchEnd: (ev: TouchEvent) => void;

	constructor(scroller: any) {
		super(scroller);
		this.handleTouchStart = this.handleTouchStart.bind(this);
		this.handleTouchMove = this.handleTouchMove.bind(this);
		this.handleTouchEnd = this.handleTouchEnd.bind(this);

		const {binder} = this.scroller;
		binder.addBinding("touchstart", this.handleTouchStart);
		binder.addBinding("touchmove", this.handleTouchMove);
		binder.addBinding("touchend", this.handleTouchEnd);
		binder.addBinding("touchcancel", this.handleTouchEnd);
	}

	touch: ?{
		identifier: any;
		initialScroll: number;
		initialX: number;
		initialY: number;
		kineticsX: any;
		kineticsY: any;
		hasAxis: boolean;
		horizontal: boolean;
	} = null;

	findTouch(ev: TouchEvent) {
		if (!this.touch) { return null; }
		const id = this.touch.identifier;
		for (let i = 0; i < ev.changedTouches.length; i++) {
			const touch = ev.changedTouches[i];
			if (touch.identifier === id) {
				return touch;
			}
		}
		return null;
	}

	handleTouchStart(ev: TouchEvent) {
		// only do touch scrolling with one finger. when a second finger touches,
		// do snapping and leave the touch event be, so it can be used for pinching
		if (this.touch) {
			this.performTouchEnd();
			return;
		}
		const {scroller} = this;
		if (!scroller.setScrollHandler(this)) { return; }
		const touch = ev.changedTouches[0];
		const {currentScroll} = scroller;
		const kineticsX = new Kinetics();
		kineticsX.push(-touch.screenX);
		const kineticsY = new Kinetics();
		kineticsY.push(-touch.screenY);
		this.touch = {
			identifier: touch.identifier,
			initialScroll: currentScroll,
			initialX: touch.clientX,
			initialY: touch.clientY,
			kineticsX, kineticsY,
			hasAxis: false,
			horizontal: false,
		};
		if (scroller.spring.running) {
			scroller.spring.reset({scroll: currentScroll});
		}
	}

	handleTouchMove(ev: TouchEvent) {
		if (ev.defaultPrevented) { return; }
		const touch = this.findTouch(ev);
		const thisTouch = this.touch;
		if (!touch || !thisTouch) { return; }

		const {scroller} = this;
		const {horizontal} = scroller;

		const {initialX, initialY, initialScroll} = thisTouch;
		const {hasAxis} = thisTouch;
		const x = touch.clientX;
		const y = touch.clientY;
		const diffX = initialX - x;
		const diffY = initialY - y;
		if (!hasAxis) {
			thisTouch.hasAxis = true;
			thisTouch.horizontal = Math.abs(diffX) > Math.abs(diffY);
		}
		if (thisTouch.horizontal !== horizontal) { return; }
		thisTouch.kineticsX.push(-x);
		thisTouch.kineticsY.push(-y);

		ev.stopPropagation();
		ev.preventDefault();

		const nextScroll = initialScroll + (horizontal ? diffX : diffY);
		if (!scroller.clampOverflow(initialScroll, nextScroll)) {
			scroller.spring.reset({scroll: nextScroll});
		}
	}

	handleTouchEnd(ev: TouchEvent) {
		const touch = this.findTouch(ev);
		if (!touch) { return; }
		this.performTouchEnd();
	}

	performTouchEnd() {
		if (!this.touch) { return; }
		const {scroller} = this;
		const {kineticsX, kineticsY, horizontal} = this.touch;
		const kinetics = horizontal ? kineticsX : kineticsY;
		this.touch = null;
		const velocity = kinetics.getVelocity();
		const direction = velocity < 0 ? "start" : "end";
		const maxFreeScroll = scroller.getMaxFreeScrollInDirection(direction);
		const spring = scroller.spring.springs.scroll;
		spring.config = Spring.normal;
		if (!maxFreeScroll) {
			// snap to the next position
			const edge = scroller.findNearestEdge();
			if (Math.abs(velocity) >= THRESHOLD) {
				const position = edge.position +
					(direction === "start" ? -1 : 1);
				scroller.snapToPosition(position, direction === "end" ? "start" : "end");
				spring.velocity = -100 * velocity;
			} else if (edge.direction !== "middle") {
				scroller.snapToPosition(edge.position, edge.direction);
			} else {
				scroller.setScrollHandler();
			}
			return;
		}
		if (Math.abs(velocity) < 0.2) {
			// do not do inertia when the fling was waaay too slow
			scroller.setScrollHandler();
			return;
		}
		const springDistance = kinetics.getRestingPositionDelta();
		const delta = Math.sign(velocity) * Math.min(
			Math.abs(maxFreeScroll),
			Math.abs(springDistance));
		const {currentScroll} = scroller;
		spring.config = Spring.inertial;
		spring.value = currentScroll;
		spring.velocity = -Math.min(1000, Math.abs(delta) * 5) * velocity;
		scroller.spring.set({scroll: currentScroll + delta}).then(() => {
			scroller.spring.springs.scroll.config = Spring.normal;
		});
	}

	unmount() {
		const {binder} = this.scroller;
		binder.removeBinding("touchstart", this.handleTouchStart);
		binder.removeBinding("touchmove", this.handleTouchMove);
		binder.removeBinding("touchend", this.handleTouchEnd);
		binder.removeBinding("touchcancel", this.handleTouchEnd);
	}
}



// WEBPACK FOOTER //
// ./lib/scroller/touch.js